'use client';

import { useMemo, useSyncExternalStore } from 'react';

import { SALES_DEMO_FRAME_PARAM } from '@cineamo/ui/src/helpers/cookie-helper';
import { DEMO_AD_PARAM } from '@cineamo/ui/src/helpers/demo-website-url';

/** Only our own b2b app may be asked for a creative — `b2b.cineamo.com`, `b2b.cineamo.dev`, `b2b.<anything>.test`. */
const B2B_HOST = /^b2b\.(cineamo\.(com|dev)|[a-z0-9-]+\.test)$/;

type DemoAdvertisement = { movieId: string; origin: string };

const noSubscribe = () => () => {};
const getServerSnapshot = () => '';

/**
 * The film whose campaign creative this page should draw, for the b2b sales demo of that film — or `null`, which is
 * the answer for every visit that is not a sales demo inside our own frame.
 *
 * **Resolved in the browser, after hydration, and deliberately so.** Public pages here are cached as HTML by a
 * provider outside Next.js, so a server-rendered branch on a cookie or a request header would put a demo response
 * into a cache that a cinema's real visitors read — an ad its operator never booked, served to its audience. The
 * HTML is the same for everyone; only this component, running per visitor, adds anything.
 *
 * Three conditions, all required: the page is framed, the frame carries the sales-demo flag the b2b page appends,
 * and the parent is a b2b app of ours (the ad is fetched from that same app, so a demo frame opened locally asks
 * the b2b running locally).
 */
export function useDemoAdvertisement(): DemoAdvertisement | null {
    // Read through `useSyncExternalStore` with no subscription, as `Providers` reads the time zone: the server
    // snapshot is empty, the client's is the frame's own, and nothing is set in an effect. The snapshot is a string
    // because it has to be referentially stable across calls.
    const snapshot = useSyncExternalStore(noSubscribe, getSnapshot, getServerSnapshot);
    return useMemo(() => {
        const [movieId, origin] = snapshot.split(' ');
        return movieId && origin ? { movieId, origin } : null;
    }, [snapshot]);
}

function getSnapshot(): string {
    if (window.self === window.top) return '';
    const params = new URLSearchParams(window.location.search);
    const movieId = params.get(DEMO_AD_PARAM);
    if (!movieId || !/^\d+$/.test(movieId) || params.get(SALES_DEMO_FRAME_PARAM) !== '1') return '';
    // `document.referrer` is the framing page; a cross-origin parent's own location is not readable.
    const origin = b2bOrigin(document.referrer);
    return origin ? `${movieId} ${origin}` : '';
}

function b2bOrigin(referrer: string): string | undefined {
    try {
        const url = referrer ? new URL(referrer) : undefined;
        return url && B2B_HOST.test(url.hostname) ? url.origin : undefined;
    } catch {
        return undefined;
    }
}

/** Where the creative for `movieId` is rendered; `placement=layer` asks for the 1080 × 1350 one. */
export function demoAdvertisementUrl({ movieId, origin }: DemoAdvertisement, placement?: 'layer'): string {
    const query = new URLSearchParams({ film: movieId, ...(placement ? { placement } : {}) });
    return `${origin}/api/demo/ad?${query}`;
}
